import { CalculateModalConfigsBase } from 'interfaces/constants/CalculateModalConfig';
import {
  calculateArrPerFte,
  calculateBurnMultiple,
  calculateEOPArr,
  calculateExpansionArrOfGrossNew,
  calculateGAHeadPercent,
  calculateGrossDollarRetention,
  calculateNewLogoArrOfGrossNew,
  calculateNetDollarRetention,
  calculateNetMagicNumber,
  calculateNetNewArr,
  calculateOpexPerFte,
  calculateQuickRatio,
  calculateRDHeadPercent,
  calculateRuleOf40,
  calculateSMHeadPercent
} from 'utils/helpers/calculateFunctions';
import {
  BOPArrField,
  currentArrField,
  expansionArrField,
  fcfField,
  fcfMarginField,
  grossChurnArrField,
  newLogoArrField,
  netNewArrField as netNewArrFieldBase,
  smOpExField,
  yoyArrGrowthField,
  gaFteField,
  rdFteField,
  smFteField,
  totalHeadcountField,
  totalOpexField
} from './fields';
import { CalculateFieldNames } from './fieldNames';

const newLogoArrOfGrossNewFields = [newLogoArrField, expansionArrField];

const netNewArrNestedFields = [
  ...newLogoArrOfGrossNewFields,
  grossChurnArrField
];

const netNewArrField = {
  ...netNewArrFieldBase,
  showCalculateBtn: true,
  children: {
    fields: netNewArrNestedFields,
    calculate: calculateNetNewArr
  }
};

const endingArrFields = [BOPArrField, ...netNewArrNestedFields];

const eopArrField = {
  label: 'End of Period ARR',
  name: CalculateFieldNames.EOP_ARR,
  value: '',
  onChange: () => {},
  placeholder: '0',
  isNumber: true,
  character: '$',
  isPrefixCharacter: true,
  showCalculateBtn: true,
  useCommaFormat: true,
  children: {
    fields: endingArrFields,
    calculate: calculateEOPArr
  }
};

export const calculateModalConfigs: CalculateModalConfigsBase = {
  [CalculateFieldNames.ARR_PER_FTE]: {
    title: 'ARR per FTE',
    description:
      'Use this module to calculate your ARR per FTE. Please include all full-time employees in your total company headcount.',
    fields: [currentArrField, totalHeadcountField],
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.ARR_PER_FTE,
        calculatedFieldCharacter: '$',
        isPrefixCharacter: true,
        useCommaFormat: true,
        calculate: calculateArrPerFte
      }
    ]
  },
  [CalculateFieldNames.RULE_OF_40]: {
    title: 'Rule of 40',
    description:
      'This is calculated based on the YoY Growth rate and FCF Margin you submitted below.',
    fields: [yoyArrGrowthField, fcfMarginField],
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.RULE_OF_40,
        calculatedFieldCharacter: '%',
        calculate: calculateRuleOf40
      }
    ]
  },
  [CalculateFieldNames.NEW_LOGO_ARR_OF_GROSS_NEW]: {
    title: 'Gross New ARR Composition',
    description: 'Use this module to calculate your Gross New ARR Composition.',
    fields: newLogoArrOfGrossNewFields,
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.NEW_LOGO_ARR_OF_GROSS_NEW,
        fieldTitle: 'New Logo ARR as a % of Gross New',
        calculatedFieldCharacter: '%',
        calculate: calculateNewLogoArrOfGrossNew
      },
      {
        name: CalculateFieldNames.EXPANSION_ARR_OF_GROSS_NEW,
        fieldTitle: 'Expansion ARR as a % of Gross New',
        calculatedFieldCharacter: '%',
        calculate: calculateExpansionArrOfGrossNew
      }
    ]
  },
  [CalculateFieldNames.QUICK_RATIO]: {
    title: 'Quick Ratio',
    description:
      'Use this module to calculate your quick ratio (the ratio of your gross new ARR vs churned ARR).',
    fields: netNewArrNestedFields,
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.QUICK_RATIO,
        calculatedFieldCharacter: 'x',
        calculate: calculateQuickRatio
      }
    ]
  },
  [CalculateFieldNames.NET_DOLLAR_RETENTION]: {
    title: 'Net Dollar Retention',
    description:
      'Use this module to calculate your annualized net dollar retention. Please input annual figures.',
    fields: [BOPArrField, newLogoArrField, eopArrField, netNewArrField],
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.NET_DOLLAR_RETENTION,
        calculatedFieldCharacter: '%',
        calculate: calculateNetDollarRetention
      }
    ],
    descriptionParts: [
      'Use this module to calculate your annualized net dollar retention. Please input ',
      { text: 'annual', underline: true, bold: true },
      ' figures.'
    ]
  },
  [CalculateFieldNames.GROSS_DOLLAR_RETENTION]: {
    title: 'Gross Dollar Retention',
    description:
      'Use this module to calculate your gross dollar retention. Please input annual figures.',
    fields: [grossChurnArrField, BOPArrField, eopArrField],
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.GROSS_DOLLAR_RETENTION,
        calculatedFieldCharacter: '%',
        calculate: calculateGrossDollarRetention
      }
    ],
    descriptionParts: [
      'Use this module to calculate your gross dollar retention. Please input ',
      { text: 'annual', underline: true, bold: true },
      ' figures.'
    ]
  },
  [CalculateFieldNames.BURN_MULTIPLE]: {
    title: 'Burn Multiple',
    description:
      'Use this module to calculate your burn multiple. Please input annual figures.',
    fields: [fcfField, netNewArrField],
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.BURN_MULTIPLE,
        calculatedFieldCharacter: 'x',
        tooltipListTitle:
          'Burn Multiple is only calculated when FCF is negative and Net New ARR is positive.',
        calculate: calculateBurnMultiple
      }
    ],
    descriptionParts: [
      'Use this module to calculate your burn multiple. Please input ',
      { text: 'annual', underline: true, bold: true },
      ' figures.'
    ]
  },
  [CalculateFieldNames.NET_MAGIC_NUMBER]: {
    title: 'Net Magic Number',
    description:
      'Use this module to calculate your net magic number. Depending on the length of your sales cycle, feel free to use S&M OpEx from the current quarter or prior quarter.',
    fields: [smOpExField, netNewArrField],
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.NET_MAGIC_NUMBER,
        calculatedFieldCharacter: 'x',
        calculate: calculateNetMagicNumber
      }
    ]
  },
  [CalculateFieldNames.OPEX_PER_FTE]: {
    title: 'OpEx per FTE',
    description:
      'Use this module to calculate your OpEx per FTE. Please provide annual OpEx figures and include all full-time employees in your headcount.',
    fields: [totalOpexField, totalHeadcountField],
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.OPEX_PER_FTE,
        calculatedFieldCharacter: '$',
        isPrefixCharacter: true,
        useCommaFormat: true,
        calculate: calculateOpexPerFte
      }
    ]
  },
  [CalculateFieldNames.SM_HEADCOUNT]: {
    title: 'Headcount Distribution (Total FTEs) S&M',
    description:
      'Please include all S&M full-time employees (including customer success).',
    fields: [smFteField, totalHeadcountField],
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.SM_HEADCOUNT_PERCENT,
        calculatedFieldCharacter: '%',
        calculate: calculateSMHeadPercent,
        fieldTitle: 'S&M as a % of Total Headcount'
      }
    ]
  },
  [CalculateFieldNames.RD_HEADCOUNT]: {
    title: 'Headcount Distribution (Total FTEs) R&D',
    description: 'Please include all R&D full-time employees.',
    fields: [rdFteField, totalHeadcountField],
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.RD_HEADCOUNT_PERCENT,
        calculatedFieldCharacter: '%',
        calculate: calculateRDHeadPercent,
        fieldTitle: 'R&D as a % of Total Headcount'
      }
    ]
  },
  [CalculateFieldNames.GA_HEADCOUNT]: {
    title: 'Headcount Distribution (Total FTEs) G&A',
    description: 'Please include all G&A full-time employees.',
    fields: [gaFteField, totalHeadcountField],
    multipleCalculatedFields: [
      {
        name: CalculateFieldNames.GA_HEADCOUNT_PERCENT,
        calculatedFieldCharacter: '%',
        calculate: calculateGAHeadPercent,
        fieldTitle: 'G&A as a % of Total Headcount'
      }
    ]
  }
};
